// Formatting shared by every page. Times are shown in UK time whatever the viewer's zone, since
// every walk so far is in the UK and the app names walks by UK local time.
import { APP_NAME } from './brand.js'

export const METERS_PER_MILE = 1609.344
const ZONE = 'Europe/London'

export const UNITS = {
  mi: { label: 'mi', meters: METERS_PER_MILE, paceLabel: '/mi' },
  km: { label: 'km', meters: 1000, paceLabel: '/km' },
}

export function formatDistance(meters, unit, digits = 2) {
  if (meters == null) return '–'
  return `${(meters / UNITS[unit].meters).toFixed(digits)} ${UNITS[unit].label}`
}

// A stride: "73 cm" with km, "2 ft 5 in" with miles (the distance setting decides).
export function formatStride(meters, unit) {
  if (meters == null || !Number.isFinite(meters)) return '–'
  if (unit === 'km') return `${Math.round(meters * 100)} cm`
  const inches = Math.round(meters / 0.0254)
  return `${Math.floor(inches / 12)} ft ${inches % 12} in`
}

export function formatDuration(ms) {
  if (ms == null || ms < 0) return '–'
  const total = Math.round(ms / 1000)
  const h = Math.floor(total / 3600)
  const m = Math.floor((total % 3600) / 60)
  const s = total % 60
  const mm = String(m).padStart(2, '0')
  const ss = String(s).padStart(2, '0')
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`
}

// Pace as m:ss per unit. Returns '–' when there's no sensible pace (no distance, or slower than
// two hours per unit, which only happens with a broken recording).
export function formatPace(ms, meters, unit) {
  if (!ms || !meters) return '–'
  const secondsPerUnit = ms / 1000 / (meters / UNITS[unit].meters)
  if (!Number.isFinite(secondsPerUnit) || secondsPerUnit > 7200) return '–'
  const m = Math.floor(secondsPerUnit / 60)
  const s = Math.round(secondsPerUnit % 60)
  return s === 60 ? `${m + 1}:00` : `${m}:${String(s).padStart(2, '0')}`
}

export function formatDate(iso) {
  return new Date(iso).toLocaleDateString('en-GB', {
    timeZone: ZONE, weekday: 'short', day: 'numeric', month: 'short', year: 'numeric',
  })
}

export function formatTime(iso) {
  return new Date(iso).toLocaleTimeString('en-GB', { timeZone: ZONE, hour: '2-digit', minute: '2-digit' })
}

// When a photo was taken, as the walk's pages show it: "16:25" on the walk's own (UK) day, else
// with the date ("Fri 2 Oct, 21:42"), and the year too when it isn't the walk's year.
export function formatTakenAt(iso, walkStart) {
  if (!walkStart || ukDateKey(iso) === ukDateKey(walkStart)) return formatTime(iso)
  const date = new Date(iso).toLocaleDateString('en-GB', {
    timeZone: ZONE, weekday: 'short', day: 'numeric', month: 'short',
    ...(ukYear(iso) === ukYear(walkStart) ? {} : { year: 'numeric' }),
  })
  // en-GB puts a comma after the weekday only when there's a year; drop it so both read alike.
  return `${date.replace(',', '')}, ${formatTime(iso)}`
}

// "2026-10-02": the UK calendar date of a moment, for grouping walks by day.
export function ukDateKey(iso) {
  return new Date(iso).toLocaleDateString('en-CA', { timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit' })
}

// "2026-10-03T16:25:20": a moment as UK wall-clock time, the value a datetime-local input takes.
export function ukInputValue(iso) {
  const parts = Object.fromEntries(new Intl.DateTimeFormat('en-GB', {
    timeZone: ZONE, year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit', hourCycle: 'h23',
  }).formatToParts(new Date(iso)).map((p) => [p.type, p.value]))
  return `${parts.year}-${parts.month}-${parts.day}T${parts.hour}:${parts.minute}:${parts.second}`
}

// The reverse: a datetime-local value read as UK wall-clock time, back to an ISO moment (null if
// it isn't a valid time). The UK offset is found by trying the reading as UTC, then correcting.
export function ukInputToISO(value) {
  const m = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})(?::(\d{2}))?$/.exec(value ?? '')
  if (!m) return null
  const wall = Date.UTC(+m[1], +m[2] - 1, +m[3], +m[4], +m[5], +(m[6] ?? 0))
  let t = wall
  for (let i = 0; i < 2; i++) t = wall - (Date.parse(`${ukInputValue(new Date(t).toISOString())}Z`) - t)
  return new Date(t).toISOString()
}

export function ukYear(iso) {
  return Number(new Date(iso).toLocaleDateString('en-GB', { timeZone: ZONE, year: 'numeric' }))
}

export function formatNumber(n) {
  return n == null ? '–' : Math.round(n).toLocaleString('en-GB')
}

// Strava, OS Maps, AllTrails and Komoot recordings are comparisons: shown, never counted in totals
// (migrations 00005, 00006, 00020).
export const COMPARISON_SOURCES = { strava_import: 'Strava', osmaps_import: 'OS Maps', alltrails_import: 'AllTrails', komoot_import: 'Komoot' }

export function sourceLabel(walk) {
  if (walk.source === 'mmw_import') return ['MapMyWalk', walk.device_model].filter(Boolean).join(' · ')
  if (COMPARISON_SOURCES[walk.source]) {
    return [COMPARISON_SOURCES[walk.source], walk.device_model].filter(Boolean).join(' · ')
  }
  return walk.device_model ? `${APP_NAME} · ${walk.device_model}` : APP_NAME
}
